import { FunctionComponent, SVGAttributes } from 'react';

import { cn } from '@/app/shared/lib/cn';

export type TokenSelectItem = {
  address: string;
  symbol: string;
  name: string;
  Icon?: FunctionComponent<SVGAttributes<SVGElement>>;
  logoURI?: string;
};

interface TokenRowProps {
  token: TokenSelectItem;
  isSelected: boolean;
  onClick: () => void;
}

const shortenAddress = (address: string): string => {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const TokenIcon = ({ token }: { token: TokenSelectItem }) => {
  if (token.Icon) {
    const Icon = token.Icon;
    return <Icon className='h-6 w-6 shrink-0 rounded-full' aria-hidden />;
  }

  if (token.logoURI) {
    return (
      <img
        src={token.logoURI}
        alt=''
        className='h-6 w-6 shrink-0 rounded-full object-cover'
        loading='lazy'
      />
    );
  }

  return (
    <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-800 text-xs text-gray-200'>
      {token.symbol.slice(0, 2)}
    </span>
  );
};

export const TokenRow = ({ token, isSelected, onClick }: TokenRowProps) => (
  <button
    type='button'
    onClick={onClick}
    role='option'
    aria-selected={isSelected}
    className={cn(
      'group flex w-full cursor-pointer flex-col rounded-md px-5 py-3 text-left transition-colors hover:bg-elevated-card-hover',
      isSelected && 'bg-elevated-card-hover',
    )}
  >
    <div className='flex min-w-0 items-center gap-2'>
      <TokenIcon token={token} />
      <span className='truncate font-medium text-gray-200'>{token.name}</span>
    </div>
    <span className='mt-1.5 ml-8 truncate text-sm text-gray-400'>
      {token.symbol} ·{' '}
      <a
        href={`https://etherscan.io/address/${token.address}`}
        target='_blank'
        rel='noopener noreferrer'
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'transition-colors group-hover:text-gray-200 group-hover:underline',
          isSelected && 'underline',
        )}
      >
        {shortenAddress(token.address)}
      </a>
    </span>
  </button>
);
